<template>
  <div class="reschedule-data-table">
    <v-overlay :value="pageLoading" absolute z-index="3">
      <div class="d-flex flex-column align-center">
        <v-progress-circular indeterminate size="36"/>
        <div class="mt-3 white--text">{{ $t('RescheduleDataTable.loading') }}</div>
      </div>
    </v-overlay>
    <div v-if="parseInt(kind) === 2" class="d-flex flex-nowrap align-center pa-1">
      <div class="ml-auto" style="width: 320px;">
        <v-text-field v-model="searchQ" placeholder="輸入姓名或手機或民國生日(例801231)" outlined hide-details dense clearable/>
      </div>
      <v-btn class="ml-2" color="primary" @click="handleSearch">搜尋</v-btn>
    </div>
    <FloatingHorizontalScrollbar ref="floatingScrollbar">
      <v-data-table
        :headers="headers"
        :items="reserveItems"
        item-key="reserve_no"
        :height="parseInt(kind) === 1 ? 'calc(100vh - 256px)' : 'calc(100vh - 304px)'"
        :no-data-text="$t('RescheduleDataTable.noData')"
        :header-props="headerProps"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{itemsPerPageOptions: [10, 20, 50]}"
      >
        <template v-slot:item.birthday="{ item }">
          <template v-if="item.birthday">{{ parseInt(item.birthday) * 1000 | formatROCDate }}</template>
        </template>
        <template v-slot:item.start_time="{ item }">
          {{ $moment(item.start_time).add(8, 'hours').format('YYYY-MM-DD HH:mm') }}
        </template>
        <template v-slot:item.updated_time="{ item }">
          {{ $moment(item.updated_time).add(8, 'hours').format('YYYY-MM-DD HH:mm') }}
        </template>
        <template v-slot:item.reserve_change_note="{ item }">
          <span>
            {{
              (item.reserve_change_note && item.reserve_change_note.length > 20) ? item.reserve_change_note.substr(0, 20) + '...' : item.reserve_change_note
            }}
          </span>
          <v-btn icon color="primary" @click="editChangeNote(item)">
            <v-icon dark x-small>
              mdi-pencil
            </v-icon>
          </v-btn>
        </template>
        <template v-slot:item.call_clinic_status="{ item }">
          {{ item.call_clinic_status }}
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn outlined small block color="primary" class="my-1" @click="confirmReschedule(item.reserve_no)">
            {{ $t('RescheduleDataTable.alterTime') }}
          </v-btn>
          <v-btn outlined small block color="primary" class="my-1" @click="cancelReschedule(item.reserve_no)">
            {{ $t('RescheduleDataTable.withdraw') }}
          </v-btn>
        </template>
        <template v-slot:item.handle_time="{ item }">
          {{ $moment(item.handle_time).add(8, 'hours').format('YYYY-MM-DD HH:mm') }}
        </template>
        <template v-slot:item.send="{ item }">
          <v-menu offset-x left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                small
                v-bind="attrs"
                @click.prevent="editReserve = item; getSendChannelInfo($event, on)"
              >
                傳送訊息
              </v-btn>
            </template>
            <v-list dense outlined class="text-body-2">
              <v-list-item
                class="justify-center"
                @click="$refs.SendLineMessageV2.sendMessageDialog = true"
              >
                LINE訊息{{ sendLineSuffix }}
              </v-list-item>
              <v-list-item
                class="justify-center"
                @click="$refs.smsMessageV2.sendMessageDialog = true"
              >
                簡訊{{ sendSmsSuffix }}
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </FloatingHorizontalScrollbar>
    <v-dialog v-if="changeNoteDialog" max-width="500" v-model="changeNoteDialog" persistent>
      <v-card>
        <v-card-title>{{ $t('RescheduleDataTable.editNote') }}</v-card-title>
        <v-card-text>
          <v-textarea v-model="editReserve.reserve_change_note" hide-details outlined no-resize/>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" text @click="updateChangeNote">
            {{ $t('RescheduleDataTable.confirm') }}
          </v-btn>
          <v-btn text @click="changeNoteDialog=false">
            {{ $t('GENERAL.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="confirmDialog" max-width="414" v-model="confirmDialog">
      <v-card>
        <v-card-title v-html="confirmTitle"/>
        <v-card-actions class="justify-space-around">
          <v-btn text color="primary" @click="confirm">{{ $t('RescheduleDataTable.confirm') }}</v-btn>
          <v-btn text @click="reset">{{ $t('GENERAL.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SendLineMessageV2
      ref="SendLineMessageV2"
      :btnProps="{text: true, small: true}"
      :patient="{
        patientName: editReserve?.name,
        patientCode: editReserve?.patient_code,
        patientPhone: editReserve?.phone,
      }"
      style="display: none;"
    />
    <SendSmsMessageV2
      v-show="false"
      ref="smsMessageV2"
      :btnProps="{text: true}"
      :patient="{
        patientName: editReserve?.name,
        patientCode: editReserve?.patient_code,
        patientPhone: editReserve?.phone,
      }"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
  getChannelQuotaAPI, getMessageSettingAPI,
  getPatientChannelBindStatusAPI,
  getRescheduleAPI,
  getSMSQuotaChannelInfoAPI,
  updateRescheduleAPI,
  updateReserveAPI
} from '@/api';
import SendLineMessageV2 from "@/components/utils/SendLineMessageV2.vue";
import SendSmsMessageV2 from "@/components/utils/SendSmsMessageV2.vue";
import FloatingHorizontalScrollbar from "@/components/common/FloatingHorizontalScrollbar.vue";

export default {
  name: "RescheduleDataTable",
  components: {FloatingHorizontalScrollbar, SendSmsMessageV2, SendLineMessageV2},
  props: {
    kind: {
      type: [Number, String],
      default: 1,
      required: false
    }
  },
  data() {
    return {
      reserveItems: [],
      pageLoading: true,
      confirmDialog: false,
      confirmTitle: null,
      changeStatus: null,
      selectReserveId: null,
      headerProps: {
        sortByText: this.$t('RescheduleDataTable.sortByText')
      },
      changeNoteDialog: false,
      editReserve: null,
      options: {},
      total: 0,
      searchQ: null,
      sendLineSuffix: '',
      sendSmsSuffix: ''
    }
  },
  computed: {
    ...mapState({
      user: state => state.user,
    }),
    ...mapGetters([
      'axiosParamData',
      'doctorUser',
      'liffUser',
      'ownChannelLoaded',
      'hasOwnChannel'
    ]),
    headers() {
      const headers = [
        {text: this.$t('RescheduleDataTable.patientName'), align: 'center', value: 'name', sortable: false, width: 100},
        {text: this.$t('RescheduleDataTable.patientPhone'), align: 'center', value: 'phone', sortable: false, width: 120},
        {text: this.$t('ReserveTable.patientBirth'), align: 'center', value: 'birthday', sortable: false, width: 132},
        {text: this.$t('RescheduleDataTable.reserveTime'), align: 'center', value: 'start_time', width: 120},
        {text: this.$t('RescheduleDataTable.reserveDoctor'), align: 'center', value: 'doctor_name', sortable: false, width: 100},
        {text: this.$t('RescheduleDataTable.note'), align: 'center', value: 'note', sortable: false, width: 150},
        {text: this.$t('RescheduleDataTable.alterNote'), align: 'center', value: 'reserve_change_note', sortable: false, width: 150},
        {text: this.$t('RescheduleDataTable.updateTime'), align: 'center', value: 'updated_time', width: 120},
        {text: this.$t('RescheduleDataTable.alterReason'), align: 'center', value: 'change_reason', sortable: false, width: 150},
        {text: this.$t('RescheduleDataTable.whetherCall'), align: 'center', value: 'call_clinic_status', sortable: false, width: 150},
      ]
      if (this.doctorUser)
        headers[3] = {
          text: this.$t('RescheduleDataTable.reserveClinic'),
          align: 'center',
          value: 'clinic_name',
          sortable: false,
          width: 100
        }
      if (parseInt(this.kind) === 1)
        headers.push({text: this.$t('RescheduleDataTable.action'), align: 'center', value: 'action', sortable: false, width: 150})
      else
        headers.push({text: '處理時間', align: 'center', value: 'handle_time', sortable: false, width: 150})
      headers.push({text: '傳送訊息', align: 'center', value: 'send', sortable: false, width: 150})
      return headers
    },
  },
  watch: {
    options: {
      handler() {
        this.getRescheduleList()
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    ...mapActions({
      getUnreadNotice: 'getUnreadNotice',
    }),
    refreshFloatingScrollbar() {
      if (this.$refs.floatingScrollbar) {
        this.$refs.floatingScrollbar.refresh()
      }
    },
    handleSearch() {
      if (this.options.page === 1) this.getRescheduleList()
      else this.options.page = 1
    },
    getRescheduleList() {
      this.pageLoading = true
      const {page, itemsPerPage} = this.options
      getRescheduleAPI({...this.axiosParamData, limit: itemsPerPage, page, kind: this.kind, name: this.searchQ}).then(res => {
        this.reserveItems = res.data.data
        this.total = parseInt(res.data.paginate.total)
      }).finally(() => {
        this.pageLoading = false
        this.refreshFloatingScrollbar()
      })
    },
    confirmReschedule(id) {
      this.confirmTitle = this.$t('RescheduleDataTable.confirmReschedule')
      this.selectReserveId = id
      this.changeStatus = 6
      this.confirmDialog = true
    },
    cancelReschedule(id) {
      this.confirmTitle = this.$t('RescheduleDataTable.cancelReschedule')
      this.selectReserveId = id
      this.changeStatus = 1
      this.confirmDialog = true
    },
    confirm() {
      const data = {
        ...this.axiosParamData,
        attend_status: this.changeStatus
      }
      updateRescheduleAPI(this.selectReserveId, data).then(() => {
        this.getRescheduleList()
        const data = {
          ...this.axiosParamData,
          reserve_change_note: null
        }
        updateReserveAPI(this.selectReserveId, data)
        this.reset()
        this.getUnreadNotice()
      })
    },
    reset() {
      this.confirmDialog = false
      this.confirmTitle = null
      this.selectReserveId = null
      this.changeStatus = null
    },
    editChangeNote(obj) {
      this.editReserve = JSON.parse(JSON.stringify(obj))
      this.changeNoteDialog = true
    },
    updateChangeNote() {
      const data = {
        ...this.axiosParamData,
        reserve_change_note: this.editReserve.reserve_change_note
      }
      updateReserveAPI(this.editReserve.reserve_no, data).then(() => {
        this.editReserve = null
        this.changeNoteDialog = false
        this.getRescheduleList()
      })
    },
    async getSendChannelInfo(event, on) {
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData)
        this.setOwnChannel(res.data.own_channel)
      }

      const [bindStatusRes, smsQuotaRes] = await Promise.all([
        getPatientChannelBindStatusAPI(this.editReserve.patient_code, this.axiosParamData),
        getSMSQuotaChannelInfoAPI(this.axiosParamData).catch(err => err.response)
      ])

      const bindData = bindStatusRes.data
      const smsQuota = smsQuotaRes.data?.quota
      this.sendSmsSuffix = ` - 剩餘${smsQuota}點`

      let lineBindStatus = false

      if (this.hasOwnChannel) {
        lineBindStatus = bindData?.own_channel_bind_status === 1

        if (lineBindStatus) {
          const res3 = await getChannelQuotaAPI(this.axiosParamData)
          const lineQuota = Number(res3.data.quota) - Number(res3.data.total_usage)

          this.sendLineSuffix = lineQuota > 0 ? ' - 可發送' : ' - 額度不足'
        } else {
          this.sendLineSuffix = ' - 未綁定'
        }
      } else {
        lineBindStatus = bindData?.dentco_channel_bind_status === 1
        this.sendLineSuffix = lineBindStatus ? ' - 可發送' : ' - 未綁定'
      }

      await on.click(event)
    }
  }
}
</script>

<style scoped>
.reschedule-data-table {
  position: relative;
}
</style>
